import type { OptimizelyDecision, ReactSDKClient } from '@optimizely/react-sdk';

import { FEATURE_FLAGS_DISABLED_IDS } from '../envConfig';

/**
 * Org (customer) ids -- and, for callers that can only see an instance, instance
 * ids -- whose Optimizely FX flags are forced off regardless of the FX dashboard.
 * Set only in Integration, for the crowd-sourced bug-bounty org: every flag is On
 * there, so external researchers would otherwise reach non-GA features. Empty
 * everywhere else, which makes the wrapper below an identity function.
 *
 * Baked in at build time by the bundler's DefinePlugin from `.env.<environment>`,
 * hence read once at module load rather than per call.
 *
 * The frontend is NOT a security boundary -- client-side gates are bypassable, so
 * this is UX-only. The backend guard (`backend/app/services/feature_flag.py` and
 * its siblings) is the authoritative control.
 *
 * See docs/tech-spec/bug-crowd-feature-flag-suppression.md.
 */
const SUPPRESSED_IDS = new Set(
  (FEATURE_FLAGS_DISABLED_IDS || '')
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
);

/**
 * The slice of `ReactSDKClient` the wrapper touches. Declared as a `Pick` so a
 * test can supply a minimal stub without casting, while the real client
 * satisfies it structurally.
 */
type SuppressibleClient = Pick<
  ReactSDKClient,
  | 'decide'
  | 'decideAll'
  | 'decideForKeys'
  | 'getAllFeatureVariables'
  | 'getFeatureVariable'
  | 'getFeatureVariableBoolean'
  | 'getFeatureVariableDouble'
  | 'getFeatureVariableInteger'
  | 'getFeatureVariableJSON'
  | 'getFeatureVariables'
  | 'getFeatureVariableString'
  | 'getOptimizelyConfig'
  | 'isFeatureEnabled'
  | 'user'
>;

let hasWarnedAboutMissingIdentity = false;

/**
 * Whether every feature flag is force-disabled for this user's org.
 *
 * Deny-by-default with no per-flag exemptions, so a newly-introduced flag is
 * covered with no action. Matches customerId (the org) or instanceId, so a new
 * instance in the same org is covered automatically.
 *
 * When the deny list IS populated but the user carries neither id, flags are
 * evaluated normally and a warning is logged once -- failing closed would blank
 * the UI for every other org, and for our own users during the window between
 * app boot and the current-user fetch resolving.
 */
const isSuppressed = (user: ReactSDKClient['user']): boolean => {
  const attributes = user.attributes || {};
  const customerId = attributes.customerId;
  const instanceId = attributes.instanceId;

  if (!customerId && !instanceId) {
    if (!hasWarnedAboutMissingIdentity) {
      hasWarnedAboutMissingIdentity = true;
      console.warn(
        'Feature flag suppression is configured but the Optimizely user carries no customerId or instanceId; evaluating flags normally.'
      );
    }
    return false;
  }

  return (
    (typeof customerId === 'string' && SUPPRESSED_IDS.has(customerId)) ||
    (typeof instanceId === 'string' && SUPPRESSED_IDS.has(instanceId))
  );
};

/** A decision in the shape FX returns, but always off and variable-less. */
const suppressedDecision = (flagKey: string, user: ReactSDKClient['user']): OptimizelyDecision => ({
  enabled: false,
  flagKey,
  reasons: [],
  ruleKey: null,
  userContext: { attributes: user.attributes, id: user.id },
  variables: {},
  variationKey: null,
});

/**
 * Wrap an Optimizely client so every decision reads as off for the deny-listed
 * org, without touching the ~72 `useDecision` call sites.
 *
 * A Proxy rather than a subclass because the client comes from the SDK's
 * factory, and because identity has to be resolved at *call* time:
 * `OptimizelyProvider` sets the user asynchronously once the current-user fetch
 * resolves, so anything decided at module-init time would see an empty user.
 *
 * Returns the client untouched when no ids are configured, so Integration is the
 * only environment where any of this is reachable.
 */
export const withFlagSuppression = <T extends SuppressibleClient>(client: T): T => {
  if (SUPPRESSED_IDS.size === 0) return client;

  const overrides: Partial<SuppressibleClient> = {
    decide: (key: string) => suppressedDecision(key, client.user),
    decideAll: () => {
      // The real decideAll answers for every flag in the datafile. Enumerate
      // those keys from the config rather than delegating, so we don't send
      // impression events for flags we are about to force off.
      const flagKeys = Object.keys(client.getOptimizelyConfig()?.featuresMap || {});
      return Object.fromEntries(flagKeys.map(key => [key, suppressedDecision(key, client.user)]));
    },
    decideForKeys: (keys: string[]) =>
      Object.fromEntries(keys.map(key => [key, suppressedDecision(key, client.user)])),
    getAllFeatureVariables: () => ({}),
    getFeatureVariable: () => null,
    getFeatureVariableBoolean: () => null,
    getFeatureVariableDouble: () => null,
    getFeatureVariableInteger: () => null,
    getFeatureVariableJSON: () => null,
    getFeatureVariableString: () => null,
    // Not named in the spec but reached by `useFeature`, which reads a flag's
    // variables through this deprecated alias rather than getAllFeatureVariables.
    getFeatureVariables: () => ({}),
    isFeatureEnabled: () => false,
  };
  const overriddenNames = new Set<string | symbol>(Object.keys(overrides));

  return new Proxy(client, {
    get(target, property, receiver) {
      if (overriddenNames.has(property) && isSuppressed(target.user)) {
        return Reflect.get(overrides, property);
      }
      return Reflect.get(target, property, receiver);
    },
  });
};
